import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as duration from 'dayjs/plugin/duration';
import * as _ from 'lodash';
import { interval, Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss']
})
export class WidgetComponent implements OnInit, OnDestroy {

  @Input() name?: string;
  @Input() isWorking?: boolean;
  @Input() header?: string;
  @Input() refreshRatesInMinutes?: number[] = [5, 10, 15];
  @Input() refreshFunc?: () => Promise<boolean>;
  @Input() actions?: any[];

  lastUpdate?: Date;
  lastUpdateRelative?: string;

  updateMetadataRateMs = 10000;
  updateMetadataSubscription?: Subscription;

  refreshSubscription?: Subscription;
  refreshRateOptions?: WidgetRefreshRate[];
  refreshRateOption?: WidgetRefreshRate;

  configKey!: string;

  constructor() {
    dayjs.extend(relativeTime.default);
    dayjs.extend(duration.default);
  }

  ngOnInit(): void {

    // Set config key
    this.configKey = `erm.devops.widget.${this.name}.config`;

    // Load refresh rate options
    this.refreshRateOptions = _.map(this.refreshRatesInMinutes, x => {
      const unit = x === 1 ? 'minute' : 'minutes';
      return new WidgetRefreshRate({
        minutes: x,
        text: `${x} ${unit}`
      });
    });

    // Set default settings
    this.initDefaultSettings();

    // Start background refresh of metadata
    this.updateMetadataSubscription = interval(this.updateMetadataRateMs).subscribe(() => {
      this.refreshLastUpdate();
    });

    // async refresh
    setTimeout(() => {
      this.refresh();
    });
  }

  ngOnDestroy(): void {
    this.updateMetadataSubscription?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
  }

  async refresh(): Promise<void> {
    if (this.refreshFunc) {
      try {
        const success = await this.refreshFunc();
        if (success) {
          this.lastUpdate = new Date();
          this.refreshLastUpdate();
        }
      } catch (error) {
        console.warn(`Error during refresh of widget: '${this.name}'`);
        console.warn(error);
      }
    }
  }

  refreshLastUpdate(): void {
    this.lastUpdateRelative = dayjs.default().to(dayjs.default(this.lastUpdate));
  }

  initDefaultSettings(): void {
    const config = this.getConfig();

    // Init refresh rate
    const refreshRateOption = _.find(this.refreshRateOptions, x => x.minutes === config?.refreshRateInMinutes) || _.first(this.refreshRateOptions);
    this.setRefreshRate(refreshRateOption);
  }

  setRefreshRate(refreshRate?: WidgetRefreshRate): void {
    if (!refreshRate || !refreshRate.minutes) {
      return;
    }
    this.refreshRateOption = refreshRate;
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = interval(refreshRate.minutes * 60 * 1000).subscribe(() => { this.refresh(); });
    this.setConfig();
  }

  getConfig(): WidgetConfiguration | undefined {
    const json = localStorage.getItem(this.configKey);
    if (json) {
      return JSON.parse(json) as WidgetConfiguration;
    }
    return undefined;
  }

  setConfig(): void {
    const config = new WidgetConfiguration({
      refreshRateInMinutes: this.refreshRateOption?.minutes
    });
    localStorage.setItem(this.configKey, JSON.stringify(config));
  }

  clearConfig(): void {
    localStorage.removeItem(this.configKey);
  }

  reset(): void {
    this.clearConfig();
    this.initDefaultSettings();
  }
}

class WidgetConfiguration {
  refreshRateInMinutes?: number;

  public constructor(init?: Partial<WidgetConfiguration>) {
    Object.assign(this, init);
  }
}

class WidgetRefreshRate {
  minutes?: number;
  text?: string;

  public constructor(init?: Partial<WidgetRefreshRate>) {
    Object.assign(this, init);
  }
}
